import { LMS_ACCESS_SUSPENDED } from 'core/graphql/client/client';

/**
 * Почему не удалось войти на занятие — чтобы студент видел причину, а не общее
 * «Nie udało się wykonać akcji».
 */
export enum JoinLessonErrorKind {
  /** У группы (на дату занятия) нет ссылки — её ещё не задали. */
  notConfigured = 'notConfigured',
  /** ТЗ 0074: доступ приостановлен; errorLink уже ведёт на /invoices. */
  suspended = 'suspended',
  /** Токен истёк/битый: пробуем один раз после обновления токена. */
  token = 'token',
  /** Сессию вернуть не удалось (после обновления токена — разлогин). */
  sessionExpired = 'sessionExpired',
  /** Запрос не дошёл до сервера. */
  network = 'network',
  unknown = 'unknown',
}

/** Ровно эти тексты отдаёт сервер (groups/schema/schema.py, client.ts). */
export const JOIN_LINK_NOT_CONFIGURED = 'Join link is not configured for this group';
const TOKEN_ERRORS = ['Signature has expired', 'Error decoding signature'];

type ErrorLike = { message?: string | null; code?: string | null } | null | undefined;

const classifyMessage = (message?: string | null): JoinLessonErrorKind | null => {
  if (!message) {
    return null;
  }
  if (message === JOIN_LINK_NOT_CONFIGURED) {
    return JoinLessonErrorKind.notConfigured;
  }
  if (message === LMS_ACCESS_SUSPENDED) {
    return JoinLessonErrorKind.suspended;
  }
  if (TOKEN_ERRORS.includes(message)) {
    return JoinLessonErrorKind.token;
  }
  return null;
};

const classifyList = (errors?: ReadonlyArray<ErrorLike> | null): JoinLessonErrorKind | null =>
  (errors || []).reduce<JoinLessonErrorKind | null>(
    (found, err) => found || classifyMessage(err?.message) || classifyMessage(err?.code),
    null,
  );

const isSuspendedNetworkError = (networkError: any): boolean =>
  networkError?.result === LMS_ACCESS_SUSPENDED || networkError?.bodyText === LMS_ACCESS_SUSPENDED;

/**
 * Ошибка мутации входа (ApolloError или что угодно брошенное) → причина.
 * `customErrors` из ответа тоже сюда: сервер может вернуть `success: false`
 * с кодом вместо top-level ошибки.
 */
export const classifyJoinLessonError = (error: unknown): JoinLessonErrorKind => {
  const err = error as any;
  const fromGraphQL = classifyList(err?.graphQLErrors);
  if (fromGraphQL) {
    return fromGraphQL;
  }
  const networkError = err?.networkError;
  if (networkError) {
    if (isSuspendedNetworkError(networkError)) {
      return JoinLessonErrorKind.suspended;
    }
    const fromNetworkBody = classifyList(networkError?.result?.errors);
    if (fromNetworkBody) {
      return fromNetworkBody;
    }
    // Без статуса — запрос не дошёл (офлайн, обрыв, CORS). Ответ 5xx — это
    // сбой сервера, его errorLink уже показывает страницей ошибки.
    return networkError.statusCode ? JoinLessonErrorKind.unknown : JoinLessonErrorKind.network;
  }
  const fromCustom = classifyList(err?.customErrors);
  if (fromCustom) {
    return fromCustom;
  }
  return classifyMessage(err?.message) || JoinLessonErrorKind.unknown;
};

/** `customErrors` успешного HTTP-ответа (`success: false`) → причина. */
export const classifyJoinCustomErrors = (
  customErrors?: ReadonlyArray<ErrorLike> | null,
): JoinLessonErrorKind => classifyList(customErrors) || JoinLessonErrorKind.unknown;
